import {Avatar, AvatarFallback, AvatarImage} from '@/shadcn_components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shadcn_components/ui/tooltip';
import {useCan} from '@refinedev/core';
import React, {useCallback, useMemo} from 'react';
import {Link} from 'react-router-dom';

interface User {
  id: string;
  name: string;
  avatar?: string;
}

interface UserIconListProps {
  users: User[];
  maxDisplay?: number;
  size?: number;
  linkPrefix?: false | string | ((user: User) => string);
  onClickUser?: (user: User) => void;
}

export const UserIconList: React.FC<UserIconListProps> = ({
  users = [],
  maxDisplay = 3,
  size = 32,
  linkPrefix = '/users/',
  onClickUser,
}) => {
  const displayedUsers = users.slice(0, maxDisplay);
  const remainingCount = users.length - maxDisplay;

  const getLink = useCallback(
    (user: User) => {
      if (linkPrefix === false) return '#';
      if (typeof linkPrefix === 'function') {
        return linkPrefix(user);
      }
      return `${linkPrefix}${user.id}/show`;
    },
    [linkPrefix],
  );

  const {data: canViewUser} = useCan({
    resource: 'users',
    action: 'read',
  });

  const UserAvatar = ({user, onClick}: {user: User; onClick?: (user: User) => void}) => (
    <Avatar
      onClick={() => onClick?.(user)}
      className="border-2 border-background hover:-translate-y-1 transition-transform"
      style={{width: size, height: size}}>
      <AvatarImage src={user.avatar} alt={user.name} />
      <AvatarFallback className="bg-primary/10">{user.name[0]}</AvatarFallback>
    </Avatar>
  );

  return (
    <div className="flex items-center -space-x-2">
      <TooltipProvider>
        {displayedUsers.map((user, index) => (
          <Tooltip key={user.id}>
            <TooltipTrigger>
              {linkPrefix === false || canViewUser?.can === false ? (
                <UserAvatar user={user} onClick={onClickUser} />
              ) : (
                <Link to={getLink(user)}>
                  <UserAvatar user={user} />
                </Link>
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{user.name}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger>
              <Avatar
                className="border-2 border-background hover:-translate-y-1 transition-transform"
                style={{width: size, height: size}}>
                <AvatarFallback className="bg-muted">+{remainingCount}</AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>
              <p>{remainingCount} người khác</p>
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </div>
  );
};
